import { Check, CircleDashed, CircleMinus, CircleSlash, Clock, Hand, MessageCircleQuestion, Pause, TriangleAlert, X } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import type { PipelineItem, RunStatus, SessionStatus } from '@orgus/protocol';
import { cn } from '../lib/cn.ts';
import { Tooltip } from './Tooltip.tsx';

/**
 * The plain status for list rows, table cells and timeline lanes (status.md, "Three ways to
 * draw a status"): a 16 px icon in the status colour and the state as a label. Only the three
 * tones a person may act on carry colour (working, attention, danger); success is green only
 * where success is the news (a landing queue); everything else is grey and told apart by its
 * icon. Never filled: the soft pill (StatusPill) is for drawer and page headers.
 */
export type RowTone = 'attention' | 'danger' | 'working' | 'success' | 'neutral';

export type RowStatusIcon = 'needs-you' | 'asked' | 'warning' | 'failed' | 'working' | 'done' | 'idle' | 'ended' | 'stopped' | 'queued' | 'paused' | 'offline';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

/** The working icon: a ring with a turning arc; still under reduced motion. */
function Spinner({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.75" />
      <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" className="origin-center animate-spin motion-reduce:animate-none" />
    </svg>
  );
}

/** A hollow dot: idle. */
function Hollow({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden>
      <circle cx="8" cy="8" r="4.5" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

const ICONS: Record<RowStatusIcon, IconType> = {
  'needs-you': Hand,
  /** A turn that ended on a question with nothing held (ADR 0056): neutral, never amber. */
  asked: MessageCircleQuestion,
  warning: TriangleAlert,
  failed: X,
  working: Spinner,
  done: Check,
  idle: Hollow,
  ended: CircleMinus,
  stopped: CircleSlash,
  queued: Clock,
  paused: Pause,
  offline: CircleDashed,
};

const TONE: Record<RowTone, { icon: string; label: string }> = {
  attention: { icon: 'text-status-attention', label: 'font-medium text-status-attention' },
  danger: { icon: 'text-status-danger', label: 'font-medium text-status-danger' },
  working: { icon: 'text-status-working', label: 'text-status-working' },
  success: { icon: 'text-status-success', label: 'text-status-success' },
  neutral: { icon: 'text-fg-subtle', label: 'text-fg-subtle' },
};

export interface RowStatusProps {
  tone: RowTone;
  icon: RowStatusIcon;
  /** The state, in words: "Needs you", "Working", "Failed". */
  label: string;
  /**
   * Hide the label: always (true), or below a breakpoint. The label then becomes the
   * tooltip and the accessible name, so the state is never colour alone.
   */
  hideLabel?: boolean | 'below-lg' | 'below-xl';
  /** With a hidden label, the icon takes focus to show its tooltip; off inside a button or link. */
  focusable?: boolean;
  className?: string;
}

// not-sr-only resets white-space to normal: keep a revealed label on one line.
const HIDE = { 'below-lg': 'sr-only lg:not-sr-only lg:whitespace-nowrap', 'below-xl': 'sr-only xl:not-sr-only xl:whitespace-nowrap' } as const;

/** A row's one status: icon plus label, plain. */
export function RowStatus({ tone, icon, label, hideLabel, focusable = true, className }: RowStatusProps) {
  const Icon = ICONS[icon];
  const t = TONE[tone];
  const body = (
    <span
      data-status-pill=""
      data-variant="plain"
      data-tone={tone}
      className={cn('inline-flex min-w-0 shrink-0 items-center gap-1.5 text-ui', className)}
      {...(hideLabel === true ? { role: 'img', 'aria-label': label, ...(focusable ? { tabIndex: 0 } : {}) } : {})}
    >
      <Icon className={cn('size-4 shrink-0', t.icon)} aria-hidden />
      {hideLabel !== true && <span className={cn('truncate', t.label, hideLabel && HIDE[hideLabel])}>{label}</span>}
    </span>
  );
  return hideLabel ? <Tooltip content={label}>{body}</Tooltip> : body;
}

/** A session's status for a row: needs you is the only loud one. */
export function sessionRowStatus(status: SessionStatus): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  switch (status) {
    case 'waiting':
      return { tone: 'attention', icon: 'needs-you', label: 'Needs you' };
    case 'working':
      return { tone: 'working', icon: 'working', label: 'Working' };
    case 'idle':
      return { tone: 'neutral', icon: 'idle', label: 'Idle' };
    default:
      return { tone: 'neutral', icon: 'ended', label: 'Ended' };
  }
}

/** A subagent's or workflow's status for a lane: done is neutral, failed is loud. */
export function runRowStatus(status: RunStatus): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  switch (status) {
    case 'running':
      return { tone: 'working', icon: 'working', label: 'Running' };
    case 'failed':
      return { tone: 'danger', icon: 'failed', label: 'Failed' };
    case 'done':
      return { tone: 'neutral', icon: 'done', label: 'Done' };
    default:
      return { tone: 'neutral', icon: 'stopped', label: 'Stopped' };
  }
}

/**
 * A pipeline item's status. A landing queue asks "did it land", so passed is green here
 * (status.md rule 1); queued and cancelled are grey.
 */
export function pipelineItemRowStatus(state: PipelineItem['state']): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  switch (state) {
    case 'active':
      return { tone: 'working', icon: 'working', label: 'In progress' };
    case 'failed':
      return { tone: 'danger', icon: 'failed', label: 'Failed' };
    case 'passed':
      return { tone: 'success', icon: 'done', label: 'Passed' };
    case 'queued':
      return { tone: 'neutral', icon: 'queued', label: 'Queued' };
    default:
      return { tone: 'neutral', icon: 'stopped', label: 'Cancelled' };
  }
}
